import { Component, type ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';

interface State {
  error: Error | null;
}

/**
 * Top-level catch-all so a render-time exception in any route shows an
 * actionable "something went wrong" screen instead of unmounting the whole
 * tree to a blank page. Mount this around <App /> in main.tsx; individual
 * routes are free to add their own narrower boundaries on top.
 */
export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: { componentStack?: string | null }) {
    console.error('[ErrorBoundary] render-time error:', error, info);
  }

  reset = () => this.setState({ error: null });

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              <CardTitle className="text-base">Something went wrong</CardTitle>
            </div>
            <CardDescription>
              A render-time error broke this page. Full stack trace is in the browser console.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <pre className="mb-3 max-h-40 overflow-auto rounded bg-muted p-2 text-[11px]">
              {this.state.error.message}
            </pre>
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => window.location.reload()}
              >
                Reload
              </Button>
              <Button variant="ghost" className="flex-1" onClick={this.reset}>
                Dismiss
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
}
